import { useTaskFiltersStore } from '@/entities/task/model/stores/task-filters';
import { cn } from '@/shared/lib/cn';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/Dialog';
import { MeetingList } from '@/widgets/meeting';
import { CreateMeetingFromSlot } from '@/widgets/slot';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { X } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useState } from 'react';

type MeetingTab = 'Встречи' | 'Слоты';

type MeetingSlotModalProps = {
  children: ReactNode;
};

const tabs: MeetingTab[] = ['Встречи', 'Слоты'];
const DAYS_TO_SHOW = 14;

function getWeekdayLabel(date: Date) {
  return format(date, 'EEE', { locale: ru })
    .slice(0, 2)
    .replace(/^./, (char) => char.toUpperCase());
}

export const MeetingSlotModal = ({ children }: MeetingSlotModalProps) => {
  const [activeTab, setActiveTab] = useState<MeetingTab>('Встречи');
  const { selectedDate, setSelectedDate } = useTaskFiltersStore();

  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(new Date());
    }
  }, [selectedDate, setSelectedDate]);

  // 14 дней начиная с сегодняшнего (включительно)
  const days = useMemo(() => {
    const start = startOfDay(new Date());
    return Array.from({ length: DAYS_TO_SHOW }, (_, i) => addDays(start, i));
  }, []);

  const selected = selectedDate ?? days[0];

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="flex w-[1010px] max-w-[calc(100vw-2rem)] flex-col gap-10 rounded-[20px] bg-white px-6 py-11 font-roboto text-black shadow-[0px_0px_11px_rgba(51,193,231,0.4),0px_0px_13px_rgba(51,193,231,0.4)] duration-300 ease-out"
      >
        {/* Заголовок */}
        <div className="flex w-full items-center justify-between animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both delay-75 duration-300">
          <DialogTitle className="text-[24px] font-semibold leading-[130%] tracking-[-0.5px] text-black">
            {activeTab === 'Встречи' ? 'Встречи' : 'Свободные слоты'}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Закрыть"
              className="flex size-7 items-center justify-center rounded-full text-black/70 transition hover:bg-black/5 hover:text-black"
            >
              <X className="size-5" />
            </button>
          </DialogClose>
        </div>

        {/* Переключатель табов + выбор дня */}
        <div className="flex w-full flex-col gap-6 animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both delay-150 duration-300">
          <div className="mx-auto flex w-[325px] items-center justify-between">
            {tabs.map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'flex h-[33px] w-[106px] items-center justify-center rounded-full border-[0.5px] px-6 py-1 text-[12px] font-semibold leading-[130%] tracking-[-0.5px] transition',
                    active
                      ? 'border-white bg-[#142141] text-white'
                      : 'border-black bg-transparent text-black'
                  )}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Полоса дней: 14 дней от сегодня */}
          <div className="mx-auto flex max-w-full items-center gap-[12px] overflow-x-auto pb-1">
            {days.map((day) => {
              const isSelected = isSameDay(day, selected);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => setSelectedDate(day)}
                  className="flex shrink-0 cursor-pointer flex-col items-center justify-center rounded-full transition-colors"
                  style={{
                    width: 46.68,
                    height: 46.68,
                    background: isSelected ? '#142141' : '#FFFFFF',
                    boxShadow: isSelected
                      ? undefined
                      : '0px 3.53729px 12.8757px rgba(0, 0, 0, 0.06)',
                    border: isSelected ? undefined : '0.5px solid #ECECEC',
                    color: isSelected ? '#FFFFFF' : '#000000',
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: 14,
                    letterSpacing: '-0.457692px',
                  }}
                >
                  <span>{format(day, 'd')}</span>
                  <span>{getWeekdayLabel(day)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Контент таба */}
        <div className="min-h-[17.5rem] w-full animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both delay-200 duration-300">
          <div
            key={activeTab}
            className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
          >
            {activeTab === 'Встречи' ? (
              <MeetingList />
            ) : (
              <CreateMeetingFromSlot />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
